import { useState, useCallback, useMemo } from 'react';
import { GridView, GridColumn, SortState, GridPagination } from '../../../../types/datagrid';

interface UseGridViewsParams {
  gridId: string;
  initialViews?: GridView[];
  defaultView?: GridView;
  onViewChange?: (view: GridView) => void;
  onViewSave?: (view: Partial<GridView>) => void;
  storage?: 'localStorage' | 'api';
}

interface GridViewState {
  columns: GridColumn[];
  sorting: SortState[];
  filters: Record<string, any>;
  pagination: GridPagination;
}

export const useGridViews = ({
  gridId,
  initialViews = [],
  defaultView,
  onViewChange,
  onViewSave,
  storage = 'localStorage',
}: UseGridViewsParams) => {
  const [views, setViews] = useState<GridView[]>(initialViews);
  const [currentView, setCurrentView] = useState<GridView | undefined>(defaultView);

  // Load views from storage
  const loadViews = useCallback(async () => {
    try {
      if (storage === 'localStorage') {
        const stored = localStorage.getItem(`datagrid-views-${gridId}`);
        if (stored) {
          const parsedViews = JSON.parse(stored) as GridView[];
          setViews(parsedViews);

          // Load default view
          const defaultViewId = localStorage.getItem(`datagrid-default-view-${gridId}`);
          if (defaultViewId) {
            const defaultView = parsedViews.find(v => v.id === defaultViewId);
            if (defaultView) {
              setCurrentView(defaultView);
              onViewChange?.(defaultView);
            }
          }
        }
      } else {
        // API storage would be implemented here
        console.warn('API storage for views not yet implemented');
      }
    } catch (error) {
      console.error('Failed to load views:', error);
    }
  }, [gridId, storage, onViewChange]);

  // Save views to storage
  const saveViews = useCallback(
    async (viewsToSave: GridView[]) => {
      try {
        if (storage === 'localStorage') {
          localStorage.setItem(`datagrid-views-${gridId}`, JSON.stringify(viewsToSave));
        } else {
          // API storage would be implemented here
          console.warn('API storage for views not yet implemented');
        }
      } catch (error) {
        console.error('Failed to save views:', error);
      }
    },
    [gridId, storage]
  );

  // Handle view change
  const handleViewChange = useCallback(
    async (viewId: string) => {
      const view = views.find(v => v.id === viewId);
      if (view) {
        setCurrentView(view);
        onViewChange?.(view);

        // Save as default view
        if (storage === 'localStorage') {
          localStorage.setItem(`datagrid-default-view-${gridId}`, viewId);
        }
      }
    },
    [views, onViewChange, storage, gridId]
  );

  // Handle view save/create
  const handleViewSave = useCallback(
    async (viewData: Partial<GridView>) => {
      const view: GridView = {
        id: viewData.id || `view-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: viewData.name || 'Untitled View',
        type: viewData.type || 'personal',
        description: viewData.description,
        isDefault: viewData.isDefault || false,
        columns: viewData.columns || [],
        filters: viewData.filters || {},
        sorting: viewData.sorting || [],
        pagination: viewData.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
        createdBy: viewData.createdBy,
        createdAt: viewData.createdAt || new Date(),
        updatedAt: new Date(),
        permissions: viewData.permissions,
      };

      setViews(prevViews => {
        const existingIndex = prevViews.findIndex(v => v.id === view.id);
        const newViews =
          existingIndex >= 0
            ? prevViews.map((v, i) => (i === existingIndex ? view : v))
            : [...prevViews, view];

        saveViews(newViews);
        return newViews;
      });

      if (view.isDefault || !currentView) {
        setCurrentView(view);
        onViewChange?.(view);
      }

      onViewSave?.(view);
      return view;
    },
    [currentView, onViewChange, onViewSave, saveViews]
  );

  // Handle view delete
  const handleViewDelete = useCallback(
    async (viewId: string) => {
      setViews(prevViews => {
        const newViews = prevViews.filter(v => v.id !== viewId);
        saveViews(newViews);
        return newViews;
      });

      // If deleting current view, switch to another or clear
      if (currentView?.id === viewId) {
        const remainingViews = views.filter(v => v.id !== viewId);
        const nextView = remainingViews.find(v => v.isDefault) || remainingViews[0];

        if (nextView) {
          setCurrentView(nextView);
          onViewChange?.(nextView);
        } else {
          setCurrentView(undefined);
        }
      }
    },
    [currentView, views, onViewChange, saveViews]
  );

  // Handle view reset (restore to default)
  const handleViewReset = useCallback(async () => {
    if (currentView) {
      // Reset current view to its original state or a default state
      const resetView: GridView = {
        ...currentView,
        filters: {},
        sorting: [],
        updatedAt: new Date(),
      };

      setCurrentView(resetView);
      onViewChange?.(resetView);
    }
  }, [currentView, onViewChange]);

  // Create view from current grid state
  const createViewFromState = useCallback(
    (
      name: string,
      gridState: GridViewState,
      options: {
        type?: 'personal' | 'role' | 'public';
        description?: string;
        isDefault?: boolean;
      } = {}
    ) => {
      return handleViewSave({
        name,
        type: options.type || 'personal',
        description: options.description,
        isDefault: options.isDefault || false,
        columns: gridState.columns,
        sorting: gridState.sorting,
        filters: gridState.filters,
        pagination: gridState.pagination,
      });
    },
    [handleViewSave]
  );

  // Update current view with new state
  const updateCurrentViewState = useCallback(
    (gridState: Partial<GridViewState>) => {
      if (currentView) {
        const updatedView: GridView = {
          ...currentView,
          ...gridState,
          updatedAt: new Date(),
        };

        setCurrentView(updatedView);

        // Auto-save if it's a personal view
        if (updatedView.type === 'personal') {
          handleViewSave(updatedView);
        }
      }
    },
    [currentView, handleViewSave]
  );

  // Computed values
  const personalViews = useMemo(() => {
    return views.filter(v => v.type === 'personal');
  }, [views]);

  const roleViews = useMemo(() => {
    return views.filter(v => v.type === 'role');
  }, [views]);

  const publicViews = useMemo(() => {
    return views.filter(v => v.type === 'public');
  }, [views]);

  const defaultViewFromViews = useMemo(() => {
    return views.find(v => v.isDefault);
  }, [views]);

  const hasViews = views.length > 0;

  const canCreateView = true; // This would be based on permissions
  const canEditView = useCallback((view: GridView) => {
    // Logic to determine if user can edit this view
    return view.type === 'personal' || view.permissions?.canEdit === true;
  }, []);

  const canDeleteView = useCallback((view: GridView) => {
    // Logic to determine if user can delete this view
    return view.type === 'personal' || view.permissions?.canDelete === true;
  }, []);

  // Get view by ID
  const getViewById = useCallback(
    (viewId: string) => {
      return views.find(v => v.id === viewId);
    },
    [views]
  );

  // Get view summary for display
  const getViewSummary = useCallback((view: GridView) => {
    const summary = [];

    if (view.sorting.length > 0) {
      summary.push(`${view.sorting.length} sort${view.sorting.length > 1 ? 's' : ''}`);
    }

    const filterCount = Object.values(view.filters).filter(
      v => v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0)
    ).length;

    if (filterCount > 0) {
      summary.push(`${filterCount} filter${filterCount > 1 ? 's' : ''}`);
    }

    const hiddenColumns = view.columns.filter(c => !c.isVisible).length;
    if (hiddenColumns > 0) {
      summary.push(`${hiddenColumns} hidden column${hiddenColumns > 1 ? 's' : ''}`);
    }

    return summary.length > 0 ? summary.join(', ') : 'No customizations';
  }, []);

  // Initialize views on mount
  useState(() => {
    loadViews();
  });

  return {
    // State
    views,
    currentView,
    personalViews,
    roleViews,
    publicViews,
    defaultView: defaultViewFromViews,
    hasViews,

    // Actions
    handleViewChange,
    handleViewSave,
    handleViewDelete,
    handleViewReset,
    createViewFromState,
    updateCurrentViewState,
    loadViews,

    // Utilities
    canCreateView,
    canEditView,
    canDeleteView,
    getViewById,
    getViewSummary,

    // For external state updates
    setViews,
    setCurrentView: useCallback(
      (view: GridView | undefined) => {
        setCurrentView(view);
        if (view) {
          onViewChange?.(view);
        }
      },
      [onViewChange]
    ),
  };
};
